import { Component, type ReactNode } from 'react';
import { PageBreadcrumb } from '@/components/sections/PageBreadcrumb';

interface Props {
  children: ReactNode;
  /**
   * Bieżąca lokalizacja. Świadomie NIE przekazywana jako React `key` —
   * to wymuszałoby pełny unmount/remount CAŁEJ podstrony (Suspense + Switch
   * + strona) przy KAŻDEJ nawigacji, nawet bez błędu. Na iOS Safari duża
   * mutacja DOM na każdej zmianie trasy jest dokładnie tym rodzajem
   * operacji, który potrafi łamać position:fixed nagłówka/dolnej nawigacji
   * (rodzeństwa tej granicy) — więc boundary samo się "leczy" w
   * componentDidUpdate zamiast być zewnętrznie remontowane.
   */
  resetKey: string;
}

interface State {
  hasError: boolean;
}

/**
 * Globalny Error Boundary wokół treści strony (Router). Bez tego
 * nieoczekiwany błąd renderu na dowolnej podstronie — w tym w trakcie
 * checkoutu, np. przez inny niż oczekiwany kształt danych z API — dawał
 * całkowicie pustą, białą stronę bez żadnego komunikatu ani wyjścia.
 * Header i MobileBottomNav żyją poza tą granicą, więc nawigacja zostaje
 * widoczna i klikalna nawet gdy treść strony padnie.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: unknown, info: unknown) {
    console.error('[ErrorBoundary] Nieoczekiwany błąd renderu:', error, info);
  }

  componentDidUpdate(prevProps: Props) {
    // Odzyskiwanie po błędzie przy zmianie trasy — bez pełnego remountu.
    if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false });
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div
          style={{
            minHeight: '70dvh',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            padding: '24px',
            gap: 16,
          }}
        >
          <PageBreadcrumb crumbs={[{ label: 'Nie można wczytać strony' }]} />
          <p style={{ fontFamily: 'Cormorant Garamond, Georgia, serif', fontSize: 'clamp(28px,5vw,40px)', color: '#2A1F14', margin: 0 }}>
            Coś poszło nie tak
          </p>
          <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 14, color: 'rgba(42,31,20,0.6)', maxWidth: 420, margin: 0 }}>
            Przepraszamy, wystąpił nieoczekiwany błąd. Twój koszyk jest bezpieczny - spróbuj odświeżyć stronę lub wrócić na stronę główną.
          </p>
          <button
            onClick={() => { this.setState({ hasError: false }); window.location.assign('/'); }}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 8, marginTop: 8,
              padding: '13px 32px', background: '#2A1F14', color: '#fff', border: 'none',
              fontFamily: 'DM Sans, sans-serif', fontSize: 11, letterSpacing: '0.12em',
              textTransform: 'uppercase', cursor: 'pointer',
            }}
          >
            Wróć do strony głównej
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
